import { Server, Cpu, MemoryStick, HardDrive, Activity, AlertTriangle } from "lucide-react";
import { clsx } from "clsx";
import { useVMs } from "@/hooks/useVMs";
import { useNodeMetrics, useAlerts } from "@/hooks/useCapacity";
import { useActiveTenant } from "@/components/tenant/TenantSelector";
import { QuotaDisplay } from "@/components/tenant/QuotaDisplay";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { useQuery } from "@tanstack/react-query";
import { getTenantQuota } from "@/api/endpoints/tenants";

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  change?: string;
  color: string;
}

function StatCard({ label, value, icon, change, color }: StatCardProps) {
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-400">{label}</span>
        <span className={clsx("rounded-lg p-2", color)}>{icon}</span>
      </div>
      <p className="mt-2 text-2xl font-bold text-gray-100">{value}</p>
      {change && (
        <p className="mt-1 text-xs text-gray-500">{change}</p>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { data: vms, isLoading: vmsLoading } = useVMs();
  const { data: nodes, isLoading: nodesLoading } = useNodeMetrics();
  const { data: alerts } = useAlerts();
  const [tenantId] = useActiveTenant();

  const { data: quota, isLoading: quotaLoading } = useQuery({
    queryKey: ["tenant-quota", tenantId],
    queryFn: () => getTenantQuota(tenantId!),
    enabled: !!tenantId,
  });

  const totalVMs = vms?.length ?? 0;
  const runningVMs = vms?.filter((vm) => vm.status === "running").length ?? 0;
  const stoppedVMs = vms?.filter((vm) => vm.status === "stopped").length ?? 0;
  const activeAlerts = alerts?.filter((a) => !a.acknowledged).length ?? 0;

  const avgCpu =
    nodes && nodes.length > 0
      ? nodes.reduce((sum, n) => sum + n.cpu_usage, 0) / nodes.length
      : 0;

  const avgRam =
    nodes && nodes.length > 0
      ? nodes.reduce((sum, n) => sum + n.ram_usage, 0) / nodes.length
      : 0;

  if (vmsLoading || nodesLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-400">
          Cluster overview and resource summary
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total VMs"
          value={totalVMs}
          icon={<Server className="h-5 w-5 text-blue-400" />}
          change={`${runningVMs} running, ${stoppedVMs} stopped`}
          color="bg-blue-500/10"
        />
        <StatCard
          label="Avg CPU Usage"
          value={`${avgCpu.toFixed(1)}%`}
          icon={<Cpu className="h-5 w-5 text-cyan-400" />}
          change={`Across ${nodes?.length ?? 0} nodes`}
          color="bg-cyan-500/10"
        />
        <StatCard
          label="Avg RAM Usage"
          value={`${avgRam.toFixed(1)}%`}
          icon={<MemoryStick className="h-5 w-5 text-purple-400" />}
          change={`Across ${nodes?.length ?? 0} nodes`}
          color="bg-purple-500/10"
        />
        <StatCard
          label="Active Alerts"
          value={activeAlerts}
          icon={<AlertTriangle className="h-5 w-5 text-yellow-400" />}
          change={activeAlerts > 0 ? "Needs attention" : "All clear"}
          color="bg-yellow-500/10"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Node Status */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-200">
              <Activity className="h-5 w-5 text-blue-400" />
              Cluster Nodes
            </h2>
            {nodes && nodes.length > 0 ? (
              <div className="space-y-4">
                {nodes.map((node) => (
                  <div
                    key={node.node}
                    className="rounded-lg border border-gray-800 p-4"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-200">
                          {node.node}
                        </span>
                      </div>
                      <span
                        className={clsx(
                          "rounded-full px-2 py-0.5 text-xs font-medium",
                          node.status === "online"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                        )}
                      >
                        {node.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="mb-1 flex items-center justify-between text-xs">
                          <span className="text-gray-500">CPU</span>
                          <span className="text-gray-300">
                            {node.cpu_usage.toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-gray-800">
                          <div
                            className="h-full rounded-full bg-blue-500"
                            style={{ width: `${node.cpu_usage}%` }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between text-xs">
                          <span className="text-gray-500">RAM</span>
                          <span className="text-gray-300">
                            {node.ram_usage.toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-gray-800">
                          <div
                            className="h-full rounded-full bg-purple-500"
                            style={{ width: `${node.ram_usage}%` }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between text-xs">
                          <span className="text-gray-500">Disk</span>
                          <span className="text-gray-300">
                            {node.disk_usage.toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-gray-800">
                          <div
                            className="h-full rounded-full bg-green-500"
                            style={{ width: `${node.disk_usage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                No node data available. Check the monitoring connection.
              </p>
            )}
          </div>
        </div>

        {/* Tenant Quota */}
        <div>
          <QuotaDisplay quota={quota ?? null} isLoading={quotaLoading} />
        </div>
      </div>

      {/* Recent VMs */}
      <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-200">
          <HardDrive className="h-5 w-5 text-green-400" />
          Recent Virtual Machines
        </h2>
        {vms && vms.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Name
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Tier
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {vms.slice(0, 5).map((vm) => (
                  <tr key={vm.id}>
                    <td className="px-3 py-2 font-medium text-gray-200">
                      {vm.name}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={clsx(
                          "rounded-full px-2 py-0.5 text-xs font-medium",
                          vm.status === "running"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-gray-500/20 text-gray-400"
                        )}
                      >
                        {vm.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-300">
                      Tier {vm.service_tier}
                    </td>
                    <td className="px-3 py-2 text-gray-400">
                      {new Date(vm.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No VMs provisioned yet.</p>
        )}
      </div>
    </div>
  );
}
